import { useCallback, useEffect, useRef } from 'react';
import type { FormInstance } from 'antd';
import { defaultCareStaffIdForMode } from '../utils/careStaffField';

interface Params {
  form: FormInstance;
  /** Loại đơn đang mở (`user_type === organization`). */
  isB2B: boolean;
  /** Id tab đang mở — để phân biệt "đổi chế độ" với "chuyển tab". */
  tabId: string;
  currentUserId?: string;
  /** `isB2BSalesUser(user)` — người đăng nhập có phải NVKD B2B. */
  currentUserIsB2BSales: boolean;
  /**
   * TCR-2429 — id của toàn bộ NVKD B2B (cùng nguồn cache với ô chọn nhân viên,
   * `GET /pos/staff?sales_channel=b2b`). Dùng để xác thực `care_staff_id` khôi phục từ snapshot.
   */
  b2bStaffIds?: readonly string[];
  /**
   * Danh sách NVKD đã có kết quả CUỐI (thành công hoặc lỗi). Chỉ khi đó mới kết luận được một id
   * là hợp lệ hay không — lúc đang tải mà xoá luôn là xoá oan.
   */
  b2bStaffResolved: boolean;
}

interface ModeSnapshot {
  tabId: string;
  isB2B: boolean;
  /** `care_staff_id` trong snapshot của tab vừa nạp (nếu có) — cần xác thực lại với đơn B2B. */
  careStaffId?: string;
}

/**
 * TCR-2429 — đồng bộ `care_staff_id` mỗi khi CHẾ ĐỘ đơn đổi giữa B2C và B2B.
 *
 * Hai danh sách chọn người khác hẳn nhau (NV thuộc chi nhánh vs NVKD GT/MT toàn công ty), nên
 * giá trị đang chọn ở chế độ cũ không còn chắc hợp lệ ở chế độ mới — xem
 * `defaultCareStaffIdForMode` để biết vì sao đặt lại mặc định thay vì giữ nguyên.
 *
 * Tách thành hook vì có hai đường đổi chế độ (bấm toggle Loại khách hàng trong
 * `CustomerSidebar`, và chọn một khách có `user_type` khác) cộng thêm đường NẠP LẠI TAB từ
 * snapshot. Đường cuối cùng là cái bẫy: nạp tab cũng làm `isB2B` đổi giá trị y như người dùng
 * vừa bấm đổi chế độ, nhưng `care_staff_id` trong snapshot là giá trị ĐÚNG của tab đó — đặt
 * lại mặc định ở đây là xoá mất lựa chọn người dùng đã lưu. Vì thế nơi nạp tab phải gọi
 * `syncMode` ngay sau khi đổ snapshot: coi chế độ vừa nạp là "đã biết", không phải "vừa đổi".
 *
 * TCR-2429 (Codex finding 2) — "đúng của tab" không có nghĩa là "hợp lệ với chế độ hiện tại":
 * snapshot có thể mang `care_staff_id` của một NV chi nhánh (đơn B2C cũ) hoặc một người đã thôi
 * làm NVKD. Rule required chỉ kiểm tra "có giá trị" nên những id đó lọt qua và BE ghi doanh số
 * cho người không thuộc danh sách NVKD. Vì vậy mỗi lần nạp tab B2B, id khôi phục được đối chiếu
 * với danh sách NVKD đã tải sẵn (không gọi thêm endpoint): không có trong danh sách, hoặc không
 * xác thực được (danh sách lỗi) ⇒ xoá và áp dụng lại quy tắc mặc định của chế độ.
 */
export function useCareStaffModeSync({
  form,
  isB2B,
  tabId,
  currentUserId,
  currentUserIsB2BSales,
  b2bStaffIds,
  b2bStaffResolved,
}: Params): {
  syncMode: (snapshot: ModeSnapshot) => void;
} {
  // Khởi tạo bằng chính giá trị lần render đầu: mount không phải "đổi chế độ".
  const modeRef = useRef<ModeSnapshot>({ tabId, isB2B });
  // Tab vừa nạp còn id chờ xác thực. Chỉ xác thực MỘT lần cho mỗi snapshot: danh sách NVKD bị
  // giới hạn 100 bản ghi, đối chiếu liên tục có thể xoá oan lựa chọn hợp lệ ngoài trang đầu.
  const pendingRestoreRef = useRef<{ tabId: string; careStaffId: string } | null>(null);

  const syncMode = useCallback((snapshot: ModeSnapshot) => {
    modeRef.current = { tabId: snapshot.tabId, isB2B: snapshot.isB2B };
    pendingRestoreRef.current =
      snapshot.isB2B && snapshot.careStaffId
        ? { tabId: snapshot.tabId, careStaffId: snapshot.careStaffId }
        : null;
  }, []);

  useEffect(() => {
    const prev = modeRef.current;
    modeRef.current = { tabId, isB2B };
    // Chuyển tab: giá trị thuộc snapshot của tab mới (nơi nạp tab đã gọi `syncMode`), không đụng.
    if (prev.tabId !== tabId) return;
    if (prev.isB2B === isB2B) return;

    const nextCareStaffId = defaultCareStaffIdForMode({
      isB2B,
      currentUserId,
      currentUserIsB2BSales,
    });
    // `setFieldValue` (không phải `setFieldsValue`) để chỉ đụng đúng field này, không ghi đè
    // các field khác mà `CustomerSidebar` vừa reset trong cùng một nhịp.
    form.setFieldValue('care_staff_id', nextCareStaffId);
  }, [form, isB2B, tabId, currentUserId, currentUserIsB2BSales]);

  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending) return;
    // Đã đổi tab trong lúc chờ danh sách: id của tab cũ không còn liên quan.
    if (pending.tabId !== tabId) {
      pendingRestoreRef.current = null;
      return;
    }
    if (!b2bStaffResolved) return;

    pendingRestoreRef.current = null;
    // Đọc giá trị HIỆN TẠI của form, không phải giá trị trong snapshot: người dùng có thể đã
    // chọn lại một NVKD khác trong lúc danh sách đang tải — lựa chọn mới đó không được xoá.
    const current = form.getFieldValue('care_staff_id') as string | undefined;
    if (!current) return;
    if (b2bStaffIds?.includes(current)) return;

    form.setFieldValue(
      'care_staff_id',
      defaultCareStaffIdForMode({ isB2B: true, currentUserId, currentUserIsB2BSales }),
    );
  }, [form, tabId, b2bStaffResolved, b2bStaffIds, currentUserId, currentUserIsB2BSales]);

  return { syncMode };
}
